import { useEffect, useState } from "react";
import { Alert, Box, Grid } from "@mui/material";
import { User } from "src/redux/state/User";
import { IMosaicTabNode, IMosaicNode } from "src/utils/MosaicTree";
import { PolicyResource, PolicyAction, PolicyName, IPolicyDoc } from "@vmosolutions/oc-interfaces";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { NavLinks } from "./NavLinks";
import { AlertDialog } from "src/components/AlertDialog";
import { isDevEnv } from "src/utils";
import EngineeringIcon from "@mui/icons-material/Engineering";

interface IAppLaunchpadProps {
  authentication?: User;
  handleClickLink: (node: IMosaicTabNode) => void;
}

export const AppLaunchPad = (props: IAppLaunchpadProps): JSX.Element => {
  const { handleClickLink } = props;
  const authentication = useSelector((state: IRootState) => state.authentication);
  const windowManager = useSelector((state: IRootState) => state.windowManager);
  const [loaded, setLoaded] = useState<boolean>(false);
  useEffect(() => {
    const userPolicies = authentication?.attributes?.policies;
    let userPolicyCopy: IPolicyDoc[] | undefined;
    if (userPolicies?.length) {
      userPolicyCopy = [...userPolicies];
    }
    if (userPolicyCopy?.length > 1) {
      const basicPolicyIndex = userPolicyCopy.findIndex((p) => p.policyName === PolicyName.Basic);
      if (typeof basicPolicyIndex === "number" && basicPolicyIndex > -1) {
        userPolicyCopy.splice(basicPolicyIndex, 1);
      }

      if (userPolicyCopy) {
        if (userPolicyCopy.length === 1 && userPolicyCopy[0].policyName === PolicyName.CrewWebOnly) {
          const statement = userPolicyCopy[0].statements?.find((s) => s?.resource.includes("crewweb"));
          const actions = statement.action;
          const resource = statement.resource;
          const isCrewWebRedirect =
            resource?.includes(PolicyResource.CrewWeb) &&
            (actions?.includes(PolicyAction.All) || actions?.includes(PolicyAction.Use));
          if (isCrewWebRedirect) {
            window.location.replace("/crew/");
          }
        }
      }
      setLoaded(true);
    }
  }, [authentication?.attributes?.policies]);
  return (
    <Grid
      container
      spacing={0}
      wrap="nowrap"
      columns={36}
      sx={(theme) => ({
        [theme.breakpoints.up("lg")]: {
          pl: 3,
          pt: 3,
        },
        [theme.breakpoints.down("md")]: {
          p: 1,
        },

        flexGrow: 1,
        position: "relative",
        width: "100%",
        display:
          //@ts-ignore
          (windowManager.currentNode.hasOwnProperty("tabNodes") && windowManager.currentNode.tabNodes.length > 0) ||
          windowManager.currentNode.hasOwnProperty("direction")
            ? "none"
            : "flex",
      })}
    >
      <Grid
        item
        xs={36}
        md={10}
        sx={(theme) => ({
          m: 0,
          p: 0,
          minWidth: "300px",
          textOverflow: "hidden",
          overflowY: "scroll",
          height: "calc(100svh - 96px)",
        })}
      >
        {loaded && authentication.attributes.policies.length === 0 && (
          <AlertDialog
            title="No Policies in Place"
            msg="This user account has no policies applied. Please contact support for assistance"
            buttons={[]}
          />
        )}
        <NavLinks />
      </Grid>
      <Grid
        item
        xs={36}
        md={26}
        sx={(theme) => ({
          [theme.breakpoints.down("sm")]: {
            display: "none",
            backgroundImage: "none",
          },
          backgroundImage: "url(/assets/launchpad/operational-control-mob-bg.svg)",
          backgroundSize: "95%",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right center",
          alignItems: "end",
          display: "flex",
          p: 4,
        })}
      >
        {isDevEnv() && (
          <Alert severity="warning" color="dev" variant="filled" icon={<EngineeringIcon />}>
            Purple items throughout the platform are only available in development environments.
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};
